<template>
    <AmModal
        :open="open"
        size="narrow"
        title="Опубликовать как готовый отчёт"
        :subtitle="`Выбрано ${messageIds.length} ${messageWord} — они будут доступны всем пользователям из сайдбара «Готовые отчёты».`"
        @close="onClose"
    >
            <div v-if="!result" class="modal-form">
                <label class="field">
                    <span class="field-label">Заголовок *</span>
                    <input
                        v-model="title"
                        type="text"
                        class="input"
                        maxlength="160"
                        placeholder="Например: «Обзор рынка ЛА · апр 2026»"
                    />
                </label>

                <label class="field">
                    <span class="field-label">Краткое описание</span>
                    <textarea
                        v-model="description"
                        class="input"
                        rows="3"
                        maxlength="500"
                        placeholder="Что в отчёте — 1-2 фразы"
                    />
                </label>

                <label class="field">
                    <span class="field-label">Категория</span>
                    <select v-model="category" class="input">
                        <option value="">Без категории</option>
                        <option value="market">Рынок</option>
                        <option value="forecast">Прогноз</option>
                        <option value="top-brands">Топ-марки</option>
                        <option value="regions">Регионы</option>
                        <option value="credit">Кредиты</option>
                        <option value="commercial">Коммерческий</option>
                        <option value="ev">EV и гибриды</option>
                        <option value="used">С пробегом</option>
                        <option value="dealers">Дилеры</option>
                    </select>
                </label>

                <div v-if="error" class="error-line">{{ error }}</div>

                <div class="modal-actions">
                    <button type="button" class="ghost-btn" @click="onClose">Отмена</button>
                    <button
                        type="button"
                        class="primary-btn"
                        :disabled="!canPublish || creating"
                        @click="onPublish"
                    >
                        <ChatIcon name="shield" :size="13" />
                        {{ creating ? 'Публикую…' : 'Опубликовать' }}
                    </button>
                </div>
            </div>

            <div v-else class="modal-form">
                <p class="success-line">Отчёт опубликован. Все пользователи увидят его в сайдбаре.</p>
                <div class="url-row">
                    <input :value="reportUrl" readonly class="input" />
                    <button type="button" class="ghost-btn" @click="copyUrl">
                        <ChatIcon name="copy" :size="13" />
                        {{ copied ? 'Скопировано' : 'Копировать' }}
                    </button>
                </div>
                <div class="modal-actions">
                    <button type="button" class="primary-btn" @click="onClose">Готово</button>
                </div>
            </div>
    </AmModal>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import AmModal from '@/components/ui/AmModal.vue'
import ChatIcon from '@/components/chat/icons/ChatIcon.vue'
import { publishPublicReport } from '@/utils/chatApi'

const props = defineProps({
    open: { type: Boolean, default: false },
    threadExternalId: { type: String, default: '' },
    messageIds: { type: Array, default: () => [] },
})

const emit = defineEmits(['close', 'published'])

const title = ref('')
const description = ref('')
const category = ref('')
const creating = ref(false)
const error = ref('')
const result = ref(null)
const copied = ref(false)

watch(() => props.open, (val) => {
    if (val) {
        title.value = ''
        description.value = ''
        category.value = ''
        creating.value = false
        error.value = ''
        result.value = null
        copied.value = false
    }
})

const canPublish = computed(() => title.value.trim().length > 0 && props.messageIds.length > 0)

const reportUrl = computed(() => {
    if (!result.value?.token) return ''
    const origin = typeof window !== 'undefined' ? window.location.origin : ''
    return `${origin}/chat/report/${result.value.token}`
})

async function onPublish() {
    if (!canPublish.value || creating.value) return
    creating.value = true
    error.value = ''
    try {
        const token = await publishPublicReport({
            threadExternalId: props.threadExternalId,
            messageIds: props.messageIds,
            title: title.value.trim(),
            description: description.value.trim() || undefined,
            category: category.value || undefined,
        })
        if (!token) throw new Error('empty response')
        result.value = { token }
        emit('published', { token })
    } catch (e) {
        error.value = e?.message || 'Не удалось опубликовать. Попробуйте ещё раз.'
    } finally {
        creating.value = false
    }
}

function copyUrl() {
    if (!reportUrl.value) return
    try {
        navigator.clipboard.writeText(reportUrl.value)
        copied.value = true
        setTimeout(() => (copied.value = false), 1800)
    } catch (e) { /* ignore */ }
}

function onClose() { emit('close') }

function plural(n, forms) {
    const abs = Math.abs(n) % 100
    if (abs >= 11 && abs <= 14) return forms[2]
    const last = abs % 10
    if (last === 1) return forms[0]
    if (last >= 2 && last <= 4) return forms[1]
    return forms[2]
}

const messageWord = computed(() => plural(props.messageIds.length, ['сообщения', 'сообщений', 'сообщений']))
</script>

<style scoped>
.modal-form {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.field { display: flex; flex-direction: column; gap: 6px; }
.field-label { font-size: 11.5px; font-weight: 600; color: #5f5f5f; text-transform: uppercase; letter-spacing: 0.4px; }
.input {
    border: 1px solid #e0e0e0; border-radius: 6px; padding: 8px 10px;
    font-family: inherit; font-size: 13px; color: #23282C; width: 100%; box-sizing: border-box; background: white;
}
.input:focus { outline: none; border-color: #23282C; }
.error-line { color: #C0050F; font-size: 12px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; padding-top: 4px; }
.ghost-btn {
    background: transparent; border: 1px solid #e0e0e0; padding: 8px 14px;
    border-radius: 6px; font-family: inherit; font-size: 13px; color: #23282C; cursor: pointer;
    display: inline-flex; align-items: center; gap: 6px;
}
.ghost-btn:hover { background: #f0f0f0; }
.primary-btn {
    display: inline-flex; align-items: center; gap: 6px;
    background: #E30613; color: white; border: 0;
    padding: 8px 14px; border-radius: 6px;
    font-family: inherit; font-size: 13px; font-weight: 600; cursor: pointer;
}
.primary-btn:hover { background: #C0050F; }
.primary-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.success-line { margin: 0; color: #0F6E56; font-size: 13px; font-weight: 500; }
.url-row { display: flex; gap: 8px; }
.url-row .input { flex: 1; background: #f7f7f7; }
</style>
